import { useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, Compass, PenTool, Target, MessageSquare, Settings,
  ChevronDown, ChevronRight, ArrowRight, ExternalLink, Sparkles, Zap,
  Calendar, Image, Hash, Film, ShoppingBag, Repeat2, Smile, Type, Megaphone,
  LayoutGrid, BookOpen, Search, Globe, Play, FileText, Mail, Phone,
  Users, TrendingUp, BarChart2, Send, Star, Clock, FolderOpen, Upload,
  Camera, Wand2, Brain, Target as TargetIcon, Rocket, PieChart,
  Lock, Bell, CreditCard, Building2, Palette, Link2, Instagram, Facebook,
  ChevronUp, Info, Lightbulb, MousePointer, Keyboard, Bot, Webhook,
  Stamp, Eye, ClipboardList, RefreshCw, AlertTriangle,
  Radar, Crosshair, Radio, CheckCircle
} from "lucide-react";

const SECTIONS = [
  {
    id: "dashboard",
    label: "Overview",
    agent: "Command Center",
    icon: LayoutDashboard,
    color: "blue",
    colorClasses: {
      bg: "bg-blue-500/10",
      border: "border-blue-500/20",
      text: "text-blue-400",
      badge: "bg-blue-500/20 text-blue-300",
      dot: "bg-blue-500",
      hover: "hover:border-blue-500/40",
    },
    path: "/",
    description: "Your bird's-eye view of everything happening across your brand.",
    features: [
      {
        icon: BarChart2,
        title: "Performance Metrics",
        desc: "See your content reach, engagement, and lead pipeline at a glance. Cards update in real time.",
      },
      {
        icon: TrendingUp,
        title: "Activity Feed",
        desc: "Recent AI actions, content published, leads captured — all timestamped in one stream.",
      },
      {
        icon: Sparkles,
        title: "Quick AI Actions",
        desc: "Jump straight into generating a post, running a strategy review, or checking your pipeline.",
      },
      {
        icon: Bell,
        title: "Notifications",
        desc: "Alerts for scheduled posts going live, new leads, and AI task completions.",
      },
    ],
    workflows: [
      "Open the dashboard to check your daily stats before starting work.",
      "Click any metric card to drill into the relevant section.",
      "Use the workspace switcher (top-left) to toggle between brands.",
    ],
  },
  {
    id: "strategy",
    label: "Draper",
    agent: "Marketing Strategy",
    icon: Compass,
    color: "purple",
    colorClasses: {
      bg: "bg-purple-500/10",
      border: "border-purple-500/20",
      text: "text-purple-400",
      badge: "bg-purple-500/20 text-purple-300",
      dot: "bg-purple-500",
      hover: "hover:border-purple-500/40",
    },
    path: "/strategy",
    description: "Your AI marketing strategist. Draper builds and maintains your brand's full marketing plan.",
    features: [
      {
        icon: Brain,
        title: "Deep Plan Wizard",
        desc: "A step-by-step interview that builds a full marketing strategy — positioning, audiences, channels, and 30/60/90-day plans. Start from the Launch button.",
      },
      {
        icon: BookOpen,
        title: "Strategy Overview",
        desc: "View your active marketing plan, brand voice, target audience, and channel breakdown all on one page.",
      },
      {
        icon: Wand2,
        title: "AI Strategy Chat",
        desc: "Ask Draper anything about your marketing strategy. The floating chat widget is available on every page.",
      },
      {
        icon: PieChart,
        title: "Platform Plans",
        desc: "See content plans broken down per social channel — posting frequency, content pillars, and format mix.",
      },
      {
        icon: TargetIcon,
        title: "Audience Profiles",
        desc: "Detailed ideal customer profiles (ICPs) generated by AI from your business data.",
      },
      {
        icon: Rocket,
        title: "Launch / Deep Plan",
        desc: "Run the full onboarding strategy wizard at any time via the '/launch' page to refresh your strategy.",
      },
    ],
    workflows: [
      "Go to /launch to run the Deep Plan wizard and generate your first complete strategy.",
      "After strategy is saved, visit /strategy to review and explore your plan.",
      "Chat with Draper (floating button) anytime to refine positioning or get campaign ideas.",
      "Your strategy auto-feeds into Peggy and Pete so content stays on-brand.",
    ],
  },
  {
    id: "content",
    label: "Peggy",
    agent: "Content Studio",
    icon: PenTool,
    color: "pink",
    colorClasses: {
      bg: "bg-pink-500/10",
      border: "border-pink-500/20",
      text: "text-pink-400",
      badge: "bg-pink-500/20 text-pink-300",
      dot: "bg-pink-500",
      hover: "hover:border-pink-500/40",
    },
    path: "/content",
    description: "Your AI content team. Peggy creates, schedules, and manages all your social content.",
    features: [
      {
        icon: Sparkles,
        title: "AI Content Generator (Post tab)",
        desc: "Pick a platform and content type, add optional context, and Peggy writes a full caption with hashtags and an image prompt. Toggle between AI mode and 'Write Your Own' manual mode.",
      },
      {
        icon: LayoutGrid,
        title: "Carousel Creator",
        desc: "Generate multi-slide carousel posts for Instagram/LinkedIn. Peggy writes each slide's headline and body text.",
      },
      {
        icon: Film,
        title: "Stories Generator",
        desc: "Create punchy single-frame Stories content optimized for vertical short-form format.",
      },
      {
        icon: Megaphone,
        title: "Ad Creative",
        desc: "Write ad copy with hooks and CTAs for Instagram, Facebook, and Google ad formats.",
      },
      {
        icon: Type,
        title: "Captions Lab",
        desc: "Generate multiple caption variations for the same post concept — pick your favorite.",
      },
      {
        icon: Hash,
        title: "Hashtag Generator",
        desc: "Get a researched hashtag set (niche + broad + branded) for any topic.",
      },
      {
        icon: ShoppingBag,
        title: "E-Commerce Posts",
        desc: "Product-focused captions designed to drive clicks and sales, with price and CTA built in.",
      },
      {
        icon: Repeat2,
        title: "Repurpose Content",
        desc: "Paste any existing piece of content (blog post, YouTube script, etc.) and Peggy repurposes it into social posts.",
      },
      {
        icon: Smile,
        title: "Meme Generator",
        desc: "Generate on-brand meme concepts with text overlay suggestions.",
      },
      {
        icon: Calendar,
        title: "Content Calendar",
        desc: "Visual month/week calendar of all scheduled posts. Click any day to see or add content. Posts show platform icon and caption preview.",
      },
      {
        icon: FolderOpen,
        title: "Library & Assets",
        desc: "All your saved drafts, scheduled posts, and published content in one searchable list. Upload brand images here — they're available across all creation tools.",
      },
      {
        icon: Camera,
        title: "Post About This Photo (AI Caption from Image)",
        desc: "In the Library tab, click 'Create Post' on any image to instantly switch to manual mode with the photo pre-loaded. Peggy reads the image and writes a caption automatically.",
      },
      {
        icon: Play,
        title: "Video Studio",
        desc: "Generate short-form video scripts and hooks for Reels, TikTok, and YouTube Shorts.",
      },
      {
        icon: Search,
        title: "SEO Hub",
        desc: "Keyword research, blog outline generation, and on-page SEO recommendations.",
      },
      {
        icon: Globe,
        title: "Page Builder",
        desc: "Embed your published landing pages and site pages directly within Content Studio.",
      },
      {
        icon: BookOpen,
        title: "Blogs Tab — Generate Blog & Blog Editor",
        desc: "The Blogs tab (/content?tab=blogs) shows all AI-written blog posts with hero image thumbnail, excerpt, and SEO score badge. Click 'Generate Blog' in the header to have Peggy research a trending topic and write a full 2,000-word article — optionally enter your own topic. Each blog card opens a dedicated Blog Editor (not the social media editor) with: Preview/Edit mode toggle for the full article body rendered in structured Markdown (H2/H3 headings, paragraphs, bold, links), hero image display with 'Regenerate Image' button, editable title, editable meta description with 160-char counter, SEO score badge with explanation, and Approve/Reject/Save actions.",
      },
      {
        icon: Stamp,
        title: "Brand Stamp — Logo & Contact Info on Images",
        desc: "Bake your brand logo, phone number, email, and/or website URL as a permanent transparent panel (bottom-right) into any content image. Configure which fields to show in Settings → Brand → Brand Stamp section (per-field toggles + live preview panel). Apply per-post: click the Stamp button on any Library card, Calendar item, or Scheduled post that has an image. The stamp is composited server-side — the branded image is saved back to the content item automatically.",
      },
    ],
    workflows: [
      "Go to /content → Post tab → pick your platform → click Generate to create AI content.",
      "After generating, click 'Save as Draft' or set a date/time and click 'Schedule'.",
      "Upload brand photos in the Library tab, then click 'Create Post' on any image to get an AI caption.",
      "Check the Calendar tab to see your upcoming schedule and drag posts to different dates.",
      "Use the Repurpose tab to turn a blog post into 5 social posts instantly.",
      "Go to /content?tab=blogs → click 'Generate Blog' to have Peggy write a full blog post now.",
      "Click any blog post to open the Blog Editor → use Preview tab to read the full formatted article.",
      "In Blog Editor: click 'Generate Hero Image' if no image yet, edit meta description, then Approve.",
    ],
  },
  {
    id: "autopilot",
    label: "Autopilot",
    agent: "Autonomous Engine",
    icon: Bot,
    color: "amber",
    colorClasses: {
      bg: "bg-amber-500/10",
      border: "border-amber-500/20",
      text: "text-amber-400",
      badge: "bg-amber-500/20 text-amber-300",
      dot: "bg-amber-500",
      hover: "hover:border-amber-500/40",
    },
    path: "/guide",
    description: "Everything that runs automatically in the background — no buttons to press. Draper, Peggy, Pete, and Rogers each have autonomous loops that work 24/7 on your behalf.",
    features: [
      {
        icon: CheckCircle,
        title: "48h Pilot — 12h Checkpoint",
        desc: "After 12 hours the system should have completed 2+ autonomous heartbeats, generated at least 1 piece of content, and sent a morning brief via Telegram. Check /api/pilot-status for live milestone progress. Green = on track.",
      },
      {
        icon: CheckCircle,
        title: "48h Pilot — 24h Checkpoint",
        desc: "At 24 hours the pilot must have logged 4+ heartbeats, generated 3+ content items, and Harry must have 1+ real intelligence findings. Content must reference CPA-specific topics — not generic marketing. Check /api/pilot-status milestone24h for live progress.",
      },
      {
        icon: FileText,
        title: "Autonomous Blog Generation — How It Works",
        desc: "Every 24 hours the system checks every workspace with an active strategy. If fewer than 2 blogs were published this week, it fires. Step 1: Draper searches Google in real time for trending topics in YOUR specific industry (ecommerce, accounting, roofing, etc.). Step 2: It scans your recent lead conversations to find the most-mentioned objections (price, timing, competitors) and uses that as a content angle. Step 3: Draper hands Peggy a focused topic + full brand context. Step 4: Peggy writes the full article, scores it for SEO, revises up to 5 times until the score meets your threshold — if it still can't reach it, the post is saved as 'Needs Review' for you to edit and approve manually. Step 5: Peggy generates a hero image (always included, even for approval-required workflows). Step 6: Peggy creates 4 platform-specific social repurpose posts (LinkedIn, Instagram, X, Facebook). Everything lands in your Blogs tab awaiting your approval — or publishes automatically if auto-approve is on.",
      },
      {
        icon: TrendingUp,
        title: "Industry-Specific Topic Selection",
        desc: "The blog topic is never generic. Draper uses your workspace's industry field to target the Google Search query — so an ecommerce agency gets posts about conversion rates, AI product recommendations, and cart abandonment. A CPA firm gets posts about quarterly tax deadlines, small business write-offs, and IRS rule changes. A roofing company gets posts about storm season prep, insurance claims, and material costs. The more detail in Settings → Brand (industry, target audience), the sharper the topic selection.",
      },
      {
        icon: Zap,
        title: "Testing — How to Trigger a Blog Right Now",
        desc: "The autonomous blog fires once per day and respects a 2-per-week cap. You have three ways to trigger one immediately: (1) Go to /content?tab=blogs → click the violet 'Generate Blog' button — optionally type a topic, then click Generate. (2) Open the Peggy chat widget and say 'write a blog post about [any topic]' — this bypasses the cap and runs the full pipeline. (3) API: POST /api/agents/blog/trigger?force=true (auth required) to skip the weekly cap; add &topic=your+topic to control the subject. All three paths generate a full article, run the SEO revision loop, generate a hero image, and create 4 repurpose posts.",
      },
      {
        icon: Brain,
        title: "What Each Agent Does Autonomously",
        desc: "Draper: runs a daily marketing brief each morning, refreshes strategy with live trends every Sunday, fires the blog guardrail daily. Peggy: fills content calendar gaps every 6 hours (social posts), publishes approved posts on schedule, generates blogs via the daily guardrail. Pete: checks ad budgets daily and pauses campaigns that exceed their weekly spending envelope, resets envelopes every Monday. Rogers: sends nurture drip emails every hour to leads in active sequences, follows up on cold leads automatically.",
      },
      {
        icon: Clock,
        title: "Autonomous Schedule (No Config Needed)",
        desc: "These tasks run whether or not you configure a schedule in Settings: daily blog generation (24h), content gap-fill (every 6h), daily marketing brief (24h), nurture drips (every hour), Pete budget check (24h), strategy review (Sundays). The only thing that changes your autonomy level is the auto-approve setting — with it off, you review everything before it goes live. With it on, content publishes directly.",
      },
      {
        icon: AlertTriangle,
        title: "Weekly 2-Blog Cap & SEO Quality Gate",
        desc: "The system limits itself to 2 blogs per workspace per 7-day window. Each blog goes through up to 5 SEO revision cycles. If the score still can't reach your configured threshold (Settings → Publishing → Blog Guardrails), the post is flagged 'Needs Review' and saved to your Blogs tab — you can edit it in the Blog Editor and approve manually. The SEO score is shown on every blog card and in the editor with an amber explanation if below threshold. Repurpose posts are always created regardless of the SEO score.",
      },
    ],
    workflows: [
      "No setup required — autonomous blog generation starts working as soon as your workspace has an active marketing strategy.",
      "To trigger a blog immediately: go to /content?tab=blogs and click the 'Generate Blog' button.",
      "To trigger via chat: open Peggy (bottom right) and say 'write a blog post about [topic]'.",
      "Set your industry and target audience in Settings → Brand to improve topic relevance.",
      "Connect WordPress in Settings → Publishing to auto-publish blogs directly to your site.",
      "Set Blog Guardrails in Settings → Publishing to lock in SEO keywords, author name, and minimum score.",
      "Turn on auto-approve in Settings → Agents to let Peggy publish content without manual review.",
      "Click any blog card in the Blogs tab to open the Blog Editor — Preview mode shows the full formatted article.",
    ],
  },
  {
    id: "harry",
    label: "Harry",
    agent: "Scout Intelligence",
    icon: Radar,
    color: "blue",
    colorClasses: {
      bg: "bg-blue-500/10",
      border: "border-blue-500/20",
      text: "text-blue-400",
      badge: "bg-blue-500/20 text-blue-300",
      dot: "bg-blue-500",
    },
    path: "/harry",
    description: "Harry monitors Reddit, NewsAPI, and Hacker News 24/7 for signals relevant to your business — competitor moves, industry trends, customer pain points, and new opportunities. Every finding is AI-classified and scored so only what matters reaches your desk.",
    features: [
      {
        icon: Radar,
        title: "Today's Brief — Your Intelligence Feed",
        desc: "The Today's Brief tab (/harry) is your daily intelligence feed. Harry scans all configured sources, classifies each item into four categories — Opportunity (emerald), Threat (rose), Trend (blue), Community Buzz (purple) — and scores each finding 0–100 for relevance to your specific business. You see: source icon (Reddit/NewsAPI/Hacker News), relevance score badge, category badge, headline, 2–3 sentence AI summary of why it matters to YOUR business, and a link to the original source. Filter by source type, category, or minimum relevance score. High-priority scans run every 4 hours; standard scans run every 24 hours.",
      },
      {
        icon: Crosshair,
        title: "Finding Actions — Dismiss, Snooze, Archive, Route",
        desc: "Every finding card has four action buttons: Dismiss (remove from feed immediately), Snooze (hide for 48 hours, surfaces again automatically), Archive (move to History tab for permanent reference), and Route. Route lets you forward the intelligence directly to another agent: 'Brief Peggy' turns the finding into a content brief that Peggy uses to write a blog post or social post; 'Add to Rogers' routes Reddit community buzz to Rogers as a lead signal; 'Send to Pete' routes it as an ad note for Pete to reference when creating campaigns. Agent routing is one click.",
      },
      {
        icon: Search,
        title: "Watchlist — Custom Topic Monitoring",
        desc: "The Watchlist tab lets you add specific topics Harry should monitor in every scan. Add a topic with a keyword, label, and optional priority level. Watchlist topics are injected into the relevance-scoring prompt — Harry prioritises findings that match your watchlist over generic industry noise. Use this for competitor brand names, specific product categories, niche industry keywords, or regulatory terms. Topics can be toggled active/inactive without deleting them. No limit on watchlist size.",
      },
      {
        icon: Globe,
        title: "Sources Tab — Reddit, NewsAPI & Hacker News",
        desc: "The Sources tab shows all configured intelligence sources. Three source types: Reddit (reddit_rss) — monitor any subreddit by name (e.g. 'accounting', 'roofing', 'ecommerce') for community discussions, reviews, complaints, and questions; NewsAPI — monitor any keyword query (e.g. 'commercial roofing trends') across thousands of news publishers; Hacker News (hn_algolia) — monitor HN search queries for startup, tech, and AI signals. Add a source by selecting its type, entering the identifier, and optionally adding a display label. Sources can be disabled without deleting them. Each source is cached and deduped — you won't see the same article twice.",
      },
      {
        icon: Radio,
        title: "Urgent Alerts — Real-Time Email Notifications",
        desc: "In Harry Settings, configure urgent alert rules: set a relevance score threshold (default 90%) and choose which categories trigger immediate email alerts (Opportunity, Threat, Trend, Community Buzz). When a finding scores above the threshold AND matches an enabled category, Harry sends an email within minutes — no waiting for the daily digest. The email includes the headline, Harry's summary, and a direct link to the original source.",
      },
      {
        icon: Bell,
        title: "Daily Email Digest — Morning Intelligence Brief",
        desc: "Harry Settings also controls the daily email digest: toggle it on/off, set your preferred delivery time (hour of day), and pick your timezone. The digest emails a ranked list of the top findings from the past 24 hours — sorted by relevance score, with category badges and one-line summaries. This is separate from urgent real-time alerts — the digest gives you a calm, curated overview every morning rather than interruptions throughout the day.",
      },
      {
        icon: FileText,
        title: "History Tab — Past Briefings Archive",
        desc: "The History tab logs every Harry scan briefing — date, number of findings, sources scanned, and a summary of top signals. Archived findings (moved from Today's Brief with the Archive action) also appear here for reference. Use History to track intelligence trends over time or to revisit a finding you archived weeks ago.",
      },
    ],
    workflows: [
      "Go to /harry → Sources tab → add your first subreddit (e.g. the subreddit for your industry) and a NewsAPI keyword.",
      "Click 'Scan Now' on Today's Brief to run an immediate scan rather than waiting for the 4-hour cycle.",
      "Filter findings by 'Threat' category to quickly surface competitor moves and industry risks.",
      "Click 'Brief Peggy' on any Opportunity finding to instantly create a blog or social content brief.",
      "Add competitor brand names to the Watchlist so Harry always prioritises findings about them.",
      "Go to Harry Settings → enable the daily email digest and set your preferred delivery time.",
      "Configure Urgent Alerts for 'Threat' findings above 85% relevance to get real-time competitor warnings.",
    ],
  },
  {
    id: "ads",
    label: "Pete",
    agent: "Ad Center",
    icon: Target,
    color: "orange",
    colorClasses: {
      bg: "bg-orange-500/10",
      border: "border-orange-500/20",
      text: "text-orange-400",
      badge: "bg-orange-500/20 text-orange-300",
      dot: "bg-orange-500",
      hover: "hover:border-orange-500/40",
    },
    path: "/ads",
    description: "Pete manages your paid advertising — ad copy, creative, targeting, and campaign strategy.",
    features: [
      {
        icon: Megaphone,
        title: "Campaign Manager",
        desc: "Create and track ad campaigns across Meta (Instagram/Facebook) and Google. View status, budget, and performance.",
      },
      {
        icon: Wand2,
        title: "AI Ad Copywriter",
        desc: "Pete writes ad headlines, primary text, and CTAs optimized for your target audience and campaign objective.",
      },
      {
        icon: Image,
        title: "Creative Brief Generator",
        desc: "Generate full creative briefs for your design team — including image direction, overlay text, and size specs.",
      },
      {
        icon: TargetIcon,
        title: "Audience Targeting Suggestions",
        desc: "Pete recommends interest-based targeting sets based on your ICP from the strategy.",
      },
      {
        icon: BarChart2,
        title: "Campaign Performance",
        desc: "Connect your ad accounts to see spend, impressions, CTR, and ROAS in one place.",
      },
      {
        icon: TrendingUp,
        title: "Budget Optimizer",
        desc: "Pete analyzes your campaign data and suggests budget reallocation to improve ROAS.",
      },
    ],
    workflows: [
      "Go to /ads → click 'New Campaign' to start a new ad initiative.",
      "Use AI Copywriter to generate 3–5 variations of your ad headline and body.",
      "Connect your Meta or Google account in Settings to see live performance data.",
      "Review Pete's targeting suggestions and apply them in your ad platform.",
    ],
  },
  {
    id: "leads",
    label: "Rogers",
    agent: "Lead Desk",
    icon: MessageSquare,
    color: "green",
    colorClasses: {
      bg: "bg-green-500/10",
      border: "border-green-500/20",
      text: "text-green-400",
      badge: "bg-green-500/20 text-green-300",
      dot: "bg-green-500",
      hover: "hover:border-green-500/40",
    },
    path: "/leads",
    description: "Rogers handles your sales pipeline, lead conversations, follow-ups, and email sequences.",
    features: [
      {
        icon: Users,
        title: "Lead Pipeline",
        desc: "Kanban-style board showing every lead's stage: New → Contacted → Qualified → Proposal → Closed. Drag cards between columns.",
      },
      {
        icon: MessageSquare,
        title: "Conversation Inbox",
        desc: "All incoming messages from leads — email, form submissions, and direct messages — in a unified inbox.",
      },
      {
        icon: Mail,
        title: "Email Sequences",
        desc: "AI-written multi-step email nurture sequences. Set delays, edit copy, and Rogers sends automatically.",
      },
      {
        icon: Wand2,
        title: "AI Reply Suggestions",
        desc: "Rogers reads each lead conversation and suggests a context-aware reply you can edit and send in one click.",
      },
      {
        icon: Phone,
        title: "Booking Links",
        desc: "Generate a public booking page (/book/your-slug) for leads to schedule calls. Syncs with your calendar.",
      },
      {
        icon: Star,
        title: "Lead Scoring",
        desc: "Rogers scores each lead based on engagement and fit, surfacing the hottest opportunities first.",
      },
      {
        icon: Send,
        title: "Outbound Campaigns",
        desc: "Create targeted outreach campaigns for cold or warm lead lists with AI-personalized copy.",
      },
      {
        icon: BarChart2,
        title: "Pipeline Analytics",
        desc: "Conversion rates, average deal size, and time-in-stage metrics across your whole pipeline.",
      },
    ],
    workflows: [
      "Go to /leads → view your pipeline board. Drag a lead card to advance its stage.",
      "Click any lead to open the conversation thread and view AI reply suggestions.",
      "Set up an email sequence → write step 1, set day-2 and day-5 follow-ups, then activate.",
      "Share your /book/your-slug booking link in your email signature or social bio.",
    ],
  },
  {
    id: "settings",
    label: "Settings",
    agent: "Account & Workspace",
    icon: Settings,
    color: "slate",
    colorClasses: {
      bg: "bg-slate-500/10",
      border: "border-slate-500/20",
      text: "text-slate-400",
      badge: "bg-slate-500/20 text-slate-300",
      dot: "bg-slate-500",
      hover: "hover:border-slate-500/40",
    },
    path: "/settings",
    description: "Four tabs: Brand (business info & brand kit), Publishing (channel connections, WordPress, blog guardrails), Agents (autonomy & scheduling), Notifications (alerts & API keys).",
    features: [
      {
        icon: Building2,
        title: "Brand Tab — Business Info & Brand Kit",
        desc: "Set workspace name, brand/business name, website URL, and industry. Upload your logo, auto-pull brand colors from your website using Brand Kit Puller, and manage brand colors. Also set contact info (phone, email, city, address). At the bottom of the Brand tab: Brand Stamp — toggle the global 'Stamp logo on content' switch and individually enable/disable Logo, Phone, Email, and Website fields. A live preview panel shows exactly how the stamp panel will look over an image before you apply it.",
      },
      {
        icon: Stamp,
        title: "Brand Tab — Brand Stamp Configuration",
        desc: "The Brand Stamp bakes your logo and contact info as a permanent, branded panel (bottom-right corner) onto any content image. Configure in Settings → Brand → Brand Stamp: (1) toggle the global stamp switch, (2) enable/disable individual fields: Logo, Phone Number, Email Address, Website URL, (3) see a live preview panel showing exactly what will appear on images. Apply stamps per-post using the Stamp button on any Library card, Calendar item, or Scheduled post. Stamping is done server-side using image compositing — the stamped image is saved back to the content item.",
      },
      {
        icon: Link2,
        title: "Publishing Tab — Channel Connections",
        desc: "Connect social accounts via OAuth for direct publishing: LinkedIn, Meta (Facebook + Instagram), Instagram Business (no Facebook required), Google (Ads & Business Profile), and X (Twitter). Each connection shows its status and connected account ID.",
      },
      {
        icon: Globe,
        title: "Publishing Tab — WordPress Blog Connection",
        desc: "Connect your WordPress site for direct blog publishing. Enter your WordPress Site URL (must be HTTPS), your WordPress username, and an Application Password (generated in WordPress → Users → Profile → Application Passwords section). No plugin required — uses the native WordPress REST API.",
      },
      {
        icon: Search,
        title: "Publishing Tab — Blog Guardrails",
        desc: "Set rules Peggy follows for every blog post: author name, primary keyword, secondary keywords, target audience, content pillars, tone instructions, and a minimum SEO score threshold (slider). Guardrails ensure consistent SEO and voice across all AI-written blog content.",
      },
      {
        icon: Calendar,
        title: "Publishing Tab — Calendar Booking Settings",
        desc: "Configure your booking link slug and availability settings. Your public booking page is available at /book/[your-slug] so leads can schedule calls directly from your nurture emails or social bio.",
      },
      {
        icon: Bot,
        title: "Agents Tab — Autonomy & Scheduling",
        desc: "Set how often agents run (Manual/Daily/Weekly), enable Autonomous Mode to auto-publish to LinkedIn/Instagram/Facebook/X, toggle Auto-Approve for Peggy's content, and set per-agent autonomy levels (Manual, Smart with confidence threshold, or Always On) for Draper, Peggy, Pete, and Rogers.",
      },
      {
        icon: Clock,
        title: "Agents Tab — Nurture Goal & Audit Log",
        desc: "Set Rogers' default nurture goal: Book Appointment, Build Awareness, Drive Purchase (Coupon), or Grow Email List. View the Agent Audit Log to see the last 50 agent actions with timestamps for full transparency.",
      },
      {
        icon: Bell,
        title: "Notifications Tab — Alerts & Email Digest",
        desc: "Toggle email alerts for: Agent Run Complete, Hot Lead Alert, Agent Waiting for Input, Campaign Launch Failure. Set email digest frequency: Instant, Daily, Weekly, or Never.",
      },
      {
        icon: Webhook,
        title: "Notifications Tab — Lead Capture API",
        desc: "Generate an API key to connect your website forms to Rogers. Copy a ready-to-paste HTML form snippet, cURL example, or Zapier/Webhook config. Leads submitted via the form land instantly in your Lead Desk with full UTM attribution.",
      },
      {
        icon: Users,
        title: "Team Members",
        desc: "Invite team members to your workspace and assign roles: Owner, Editor, or Viewer. Owners can delete the workspace; Editors can make changes; Viewers can only read.",
      },
      {
        icon: CreditCard,
        title: "AI Usage & Billing",
        desc: "View your current AI credit balance, usage history, and overage billing settings. Enable overage billing so Pete and Peggy never stop mid-campaign when credits run low.",
      },
      {
        icon: TrendingUp,
        title: "Ad Budget Envelopes",
        desc: "Set Pete's weekly ad spending limit per platform (Meta, Google, LinkedIn, X). Pete auto-launches approved draft campaigns within the envelope and pauses them when the limit is reached. Resets every Monday.",
      },
      {
        icon: Palette,
        title: "Always-Visible — Audience, Voice & Social Profile Links",
        desc: "These sections are always shown on the Settings page regardless of which tab is active: Social Media Profiles (paste your LinkedIn, X, Instagram, Facebook, TikTok profile URLs for Peggy's reference), Audience & Voice (target audience description, brand voice style, and brand tone), and Ad Budget (Pete's weekly spending envelopes per platform).",
      },
    ],
    workflows: [
      "Start with the Brand tab — fill in business name, website, and industry so all AI output is accurate.",
      "Go to the Publishing tab → Channel Connections to connect Instagram, LinkedIn, or Meta for direct publishing.",
      "To connect WordPress: Publishing tab → WordPress section → enter site URL, username, and an Application Password from WordPress → Users → Profile.",
      "Set Blog Guardrails in the Publishing tab to lock in your SEO keywords, author name, and content rules for every blog post Peggy writes.",
      "Use the Agents tab to set how autonomously each agent operates — start with Manual and increase as you build trust.",
      "Generate a Lead Capture API key in the Notifications tab and paste the HTML snippet on your website to feed leads into Rogers automatically.",
      "Configure Brand Stamp in the Brand tab → enable Logo, Phone, Email, and/or Website, then apply per-post via the Stamp button on content cards.",
    ],
  },
];

const TIPS = [
  { icon: Lightbulb, tip: "Always fill out your workspace brand info in Settings first — every AI feature uses it to personalize output." },
  { icon: Zap, tip: "Run the Deep Plan wizard (/launch) before using Content Studio. Peggy uses your strategy to keep posts on-brand." },
  { icon: Camera, tip: "Upload photos to the Library, then click 'Create Post' on any image to instantly get an AI caption." },
  { icon: Keyboard, tip: "Use the floating Draper chat widget (bottom-right) on any page to ask strategy questions or get help." },
  { icon: MousePointer, tip: "Drag-and-drop lead cards on the Rogers pipeline board to advance them through stages." },
  { icon: Star, tip: "Generated content auto-applies your brand voice, audience, and strategy — the more info in Settings, the better the output." },
  { icon: Globe, tip: "Connect your WordPress site in Settings → Publishing tab to publish blog posts directly from Peggy — no plugin needed." },
  { icon: Search, tip: "Set Blog Guardrails in Settings → Publishing tab to lock in your SEO keywords and content rules — Peggy follows them on every blog post." },
  { icon: Bot, tip: "Blogs are generated automatically once per day — no setup needed. Draper picks live trending topics for your specific industry via Google Search." },
  { icon: BookOpen, tip: "Go to /content?tab=blogs → click 'Generate Blog' to trigger a blog post immediately. Optionally type a topic, or leave it blank and Peggy picks a live trending topic." },
  { icon: Eye, tip: "Click any blog post in the Blogs tab to open the Blog Editor. Use 'Preview' mode to read the full formatted article — H2/H3 headings, paragraphs, and all. Use 'Edit' mode to make changes before approving." },
  { icon: Stamp, tip: "Brand Stamp bakes your logo and contact info permanently onto images. Configure which fields to show in Settings → Brand, then click the Stamp button on any content card in the Library, Calendar, or Scheduled posts list." },
  { icon: ClipboardList, tip: "Use the 'Tester Feedback' button in the sidebar to report bugs, suggestions, or improvements. Pick the feature area, type, and severity — it goes straight into the support queue." },
  { icon: Radar, tip: "Harry (Scout Intelligence at /harry) monitors Reddit, news, and Hacker News 24/7. Add your industry subreddit and a competitor name to the Watchlist — you'll start getting classified signals within 4 hours." },
  { icon: Crosshair, tip: "When Harry surfaces an Opportunity finding, click 'Brief Peggy' to instantly convert it into a blog or content brief. Peggy will use the intelligence as the topic and angle — no extra prompting needed." },
];

function FeatureCard({ icon: Icon, title, desc, color }: { icon: typeof PenTool; title: string; desc: string; color: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <button
      onClick={() => setExpanded(!expanded)}
      className="w-full text-left bg-card/40 hover:bg-card/70 border border-border hover:border-border/80 rounded-xl p-3 transition-all group"
      data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg bg-${color}-500/10 shrink-0`}>
          <Icon size={14} className={`text-${color}-400`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-foreground">{title}</span>
            {expanded
              ? <ChevronUp size={12} className="text-muted-foreground shrink-0" />
              : <ChevronDown size={12} className="text-muted-foreground shrink-0" />
            }
          </div>
          {expanded && (
            <p className="text-xs text-muted-foreground/80 mt-1.5 leading-relaxed">{desc}</p>
          )}
        </div>
      </div>
    </button>
  );
}

function PilotStatusCard() {
  const { data, isLoading } = useQuery<any>({ queryKey: ['/api/pilot-status'] });
  const m = data?.milestone12h;
  const m24 = data?.milestone24h;
  const m48 = data?.milestone48h;

  const rows = [
    { label: 'Heartbeats (12h)', value: `${m?.heartbeats12h ?? 0}/2`, met: (m?.heartbeats12h ?? 0) >= 2 },
    { label: 'Content generated (12h)', value: `${m?.content12h ?? 0}/1`, met: (m?.content12h ?? 0) >= 1 },
    { label: 'Morning brief sent', value: m?.briefSent12h ? 'Yes' : 'No', met: !!m?.briefSent12h },
  ];

  const rows24 = [
    { label: 'Heartbeats (24h)', value: `${m24?.heartbeats24h ?? 0}/4`, met: (m24?.heartbeats24h ?? 0) >= 4 },
    { label: 'Content generated (24h)', value: `${m24?.content24h ?? 0}/3`, met: (m24?.content24h ?? 0) >= 3 },
    { label: 'Harry findings (24h)', value: `${m24?.harryFindings24h ?? 0}/1`, met: (m24?.harryFindings24h ?? 0) >= 1 },
  ];

  return (
    <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-2">
      <p className="text-[11px] font-bold text-amber-400 uppercase tracking-wider mb-2">12h Pilot Checkpoint</p>
      {isLoading ? (
        <p className="text-xs text-muted-foreground/60">Loading…</p>
      ) : (
        rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground/80">{r.label}</span>
            <div className="flex items-center gap-1.5">
              <span className={`text-xs font-semibold ${r.met ? 'text-emerald-400' : 'text-amber-400'}`}>{r.value}</span>
              <span className={`w-2 h-2 rounded-full shrink-0 ${r.met ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            </div>
          </div>
        ))
      )}
      <div className='border-t border-amber-500/10 my-2' />
      <p className="text-[11px] font-bold text-amber-400 uppercase tracking-wider mb-2">24h Pilot Checkpoint</p>
      {isLoading ? (
        <p className="text-xs text-muted-foreground/60">Loading…</p>
      ) : (
        rows24.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground/80">{r.label}</span>
            <div className="flex items-center gap-1.5">
              <span className={`text-xs font-semibold ${r.met ? 'text-emerald-400' : 'text-amber-400'}`}>{r.value}</span>
              <span className={`w-2 h-2 rounded-full shrink-0 ${r.met ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            </div>
          </div>
        ))
      )}
      <div className='border-t border-violet-500/10 my-2' />
      <p className="text-[11px] font-bold text-violet-400 uppercase tracking-wider mb-2">48h Pilot Review</p>
      {isLoading ? (
        <p className="text-xs text-muted-foreground/60">Loading…</p>
      ) : (
        <>
          {[{ label: 'Content generated (48h)', value: `${m48?.totalContent48h ?? 0}/3`, met: (m48?.totalContent48h ?? 0) >= 3 }, { label: 'Approval rate', value: (m48?.totalContent48h ?? 0) > 0 ? `${Math.round((m48?.approvalRate48h ?? 0) * 100)}%` : 'No content yet', met: (m48?.approvalRate48h ?? 0) >= 0.5 }].map((r) => (
            <div key={r.label} className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground/80">{r.label}</span>
              <div className="flex items-center gap-1.5">
                <span className={`text-xs font-semibold ${r.met ? 'text-emerald-400' : 'text-violet-400'}`}>{r.value}</span>
                <span className={`w-2 h-2 rounded-full shrink-0 ${r.met ? 'bg-emerald-500' : 'bg-violet-500'}`} />
              </div>
            </div>
          ))}
          {(m48?.totalContent48h ?? 0) > 0 && !(m48?.met) && (
            <a href="/content" className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-violet-400 hover:text-violet-300 underline underline-offset-2">
              Review &amp; approve content →
            </a>
          )}
        </>
      )}
    </div>
  );
}

function Section({ section }: { section: typeof SECTIONS[0] }) {
  const [open, setOpen] = useState(false);
  const { colorClasses } = section;
  const Icon = section.icon;

  return (
    <div
      className={`border rounded-2xl transition-all overflow-hidden ${colorClasses.border} ${open ? colorClasses.bg : "border-border"}`}
      data-testid={`guide-section-${section.id}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 p-5 text-left"
        data-testid={`guide-section-toggle-${section.id}`}
      >
        <div className={`p-2.5 rounded-xl ${colorClasses.bg} border ${colorClasses.border} shrink-0`}>
          <Icon size={18} className={colorClasses.text} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-display font-bold text-foreground">{section.label}</span>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${colorClasses.badge}`}>{section.agent}</span>
          </div>
          <p className="text-xs text-muted-foreground/70 mt-0.5 leading-relaxed line-clamp-1">{section.description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            href={section.path}
            onClick={(e) => e.stopPropagation()}
            className={`hidden sm:flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg border ${colorClasses.border} ${colorClasses.text} ${colorClasses.bg} ${colorClasses.hover} transition-colors`}
            data-testid={`guide-go-${section.id}`}
          >
            Open <ArrowRight size={10} />
          </Link>
          {open
            ? <ChevronUp size={16} className="text-muted-foreground" />
            : <ChevronDown size={16} className="text-muted-foreground" />
          }
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-5 border-t border-border/50">
          <p className="text-sm text-muted-foreground/80 pt-4 leading-relaxed">{section.description}</p>

          <div>
            <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Features & Buttons</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {section.features.map((f) => (
                <FeatureCard key={f.title} icon={f.icon} title={f.title} desc={f.desc} color={section.color} />
              ))}
            </div>
            {section.id === 'autopilot' && (
              <div className="mt-3">
                <PilotStatusCard />
              </div>
            )}
          </div>

          <div>
            <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <ChevronRight size={10} className={colorClasses.text} /> Common Workflows
            </h3>
            <div className="space-y-2">
              {section.workflows.map((w, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className={`text-[10px] font-bold mt-0.5 px-1.5 py-0.5 rounded ${colorClasses.badge} shrink-0`}>{i + 1}</span>
                  <p className="text-xs text-muted-foreground/80 leading-relaxed">{w}</p>
                </div>
              ))}
            </div>
          </div>

          <Link
            href={section.path}
            className={`sm:hidden flex items-center justify-center gap-2 w-full text-sm font-semibold py-2.5 rounded-xl border ${colorClasses.border} ${colorClasses.text} ${colorClasses.bg} ${colorClasses.hover} transition-colors`}
          >
            Go to {section.label} <ArrowRight size={14} />
          </Link>
        </div>
      )}
    </div>
  );
}

export default function Guide() {
  const [search, setSearch] = useState("");
  const [tipsOpen, setTipsOpen] = useState(true);

  const filtered = search.trim()
    ? SECTIONS.filter(
        (s) =>
          s.label.toLowerCase().includes(search.toLowerCase()) ||
          s.agent.toLowerCase().includes(search.toLowerCase()) ||
          s.description.toLowerCase().includes(search.toLowerCase()) ||
          s.features.some(
            (f) =>
              f.title.toLowerCase().includes(search.toLowerCase()) ||
              f.desc.toLowerCase().includes(search.toLowerCase())
          )
      )
    : SECTIONS;

  return (
    <div className="space-y-6" data-testid="guide-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Interactive Guide</h1>
          <p className="text-muted-foreground/70 mt-0.5 text-sm">
            Every feature, every button — click any section to expand a full breakdown.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground/50">
          <Info size={12} />
          {SECTIONS.reduce((acc, s) => acc + s.features.length, 0)} features documented
        </div>
      </div>

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/50" />
        <input
          data-testid="guide-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search features, sections, or keywords..."
          className="w-full bg-muted/40 border border-border focus:border-blue-500/50 outline-none rounded-xl pl-9 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 transition-colors"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-muted-foreground transition-colors"
          >
            ×
          </button>
        )}
      </div>

      {!search && (
        <div className={`rounded-2xl border border-yellow-500/20 overflow-hidden ${tipsOpen ? "bg-yellow-500/5" : ""}`}>
          <button
            onClick={() => setTipsOpen(!tipsOpen)}
            className="w-full flex items-center gap-3 p-4 text-left"
            data-testid="guide-tips-toggle"
          >
            <div className="p-2 rounded-lg bg-yellow-500/10">
              <Lightbulb size={14} className="text-yellow-400" />
            </div>
            <span className="text-sm font-semibold text-foreground flex-1">Pro Tips</span>
            {tipsOpen ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
          </button>
          {tipsOpen && (
            <div className="px-4 pb-4 border-t border-yellow-500/10 grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
              {TIPS.map(({ icon: Icon, tip }, i) => (
                <div key={i} className="flex items-start gap-3 bg-card/40 rounded-xl p-3 border border-border">
                  <Icon size={13} className="text-yellow-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground/80 leading-relaxed">{tip}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground/50 space-y-2">
          <Search size={32} className="mx-auto opacity-40" />
          <p className="text-sm">No features found for "{search}"</p>
          <button onClick={() => setSearch("")} className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
            Clear search
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => (
            <Section key={s.id} section={s} />
          ))}
        </div>
      )}

      <div className="text-center py-4 border-t border-border/30">
        <p className="text-xs text-muted-foreground/40">
          Found a bug or have a suggestion? Use the <span className="text-foreground/60 font-medium">Tester Feedback</span> button in the sidebar. Need help? Use the <span className="text-foreground/60 font-medium">Help</span> button.
        </p>
      </div>
    </div>
  );
}
